/**
 * SDG Attribution Panel v3 — Pre-Verification Grade, Scientifically Defensible
 *
 * Rules:
 * - Always derived from project data, NEVER manually assigned
 * - Primary metric: tCO₂e (always anchored here)
 * - Secondary: car equivalents (illustrative, clearly labelled)
 * - Confidence: High / Medium / Indicative
 * - Data Maturity: Estimated / Measured / Verified (with upgrade path)
 * - Uncertainty Range (±% with drivers)
 * - System Boundary (included/excluded)
 * - Baseline Justification (additionality argument)
 * - Evidence References (specific IPCC sections)
 * - Versioning (engine version, methodology, generated_at)
 */
import React, { useState, useEffect } from "react";
import { ChevronDown, Info, Shield, Database, AlertTriangle, Layers, BookOpen, ArrowUpCircle, Tag } from "lucide-react";

const CONFIDENCE_CONFIG = {
  High:       { color: "text-emerald-700 bg-emerald-50 border-emerald-200", dot: "bg-emerald-500" },
  Medium:     { color: "text-amber-700 bg-amber-50 border-amber-200",       dot: "bg-amber-500"   },
  Indicative: { color: "text-slate-600 bg-slate-100 border-slate-200",      dot: "bg-slate-400"   },
};

const DATA_MATURITY_CONFIG = {
  Estimated: { color: "text-orange-700 bg-orange-50 border-orange-200", dot: "bg-orange-400", icon: "~" },
  Measured:  { color: "text-blue-700 bg-blue-50 border-blue-200",       dot: "bg-blue-500",   icon: "M" },
  Verified:  { color: "text-emerald-700 bg-emerald-50 border-emerald-200", dot: "bg-emerald-500", icon: "V" },
};

function SDGBadge({ sdg, color, size = "md" }) {
  const sz = size === "sm" ? "w-7 h-7 text-[10px]" : size === "lg" ? "w-14 h-14 text-lg" : "w-10 h-10 text-sm";
  return (
    <div className={`${sz} rounded-xl flex flex-col items-center justify-center flex-shrink-0 font-bold text-white leading-none select-none`}
      style={{ backgroundColor: color || "#3F7E44" }}
      title={`SDG ${sdg}`}>
      <span style={{ fontSize: "8px", opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.5px" }}>SDG</span>
      <span style={{ fontSize: size === "sm" ? "11px" : size === "lg" ? "20px" : "14px", lineHeight: 1 }}>{sdg}</span>
    </div>
  );
}

function ConfidenceBadge({ level }) {
  const c = CONFIDENCE_CONFIG[level] || CONFIDENCE_CONFIG.Indicative;
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded-full border ${c.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {level}
    </span>
  );
}

function DataMaturityBadge({ level }) {
  const c = DATA_MATURITY_CONFIG[level] || DATA_MATURITY_CONFIG.Estimated;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded-full border ${c.color}`}
      title={`Data Maturity: ${level}`}
    >
      <Database size={8} className="flex-shrink-0" />
      {level}
    </span>
  );
}

// ── Compact summary row ────────────────────────────────────────────────────
function SDGSummaryRow({ sdgData }) {
  if (!sdgData?.attributed_sdgs?.length) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="sdg-summary-row">
      {sdgData.attributed_sdgs.map(a => (
        <div key={a.sdg} className="flex items-center gap-1.5 px-2 py-1 rounded-lg border"
          style={{ borderColor: a.color + "40", backgroundColor: a.color + "10" }}>
          <SDGBadge sdg={a.sdg} color={a.color} size="sm" />
          <div>
            <p className="text-[9px] font-bold" style={{ color: a.color }}>SDG {a.sdg}</p>
            <p className="text-[8px] text-slate-500 leading-tight">{a.title}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Individual SDG card ────────────────────────────────────────────────────
function SDGAttributionCard({ a }) {
  const [showEvidence, setShowEvidence]   = useState(false);
  const [showBoundary, setShowBoundary]   = useState(false);
  const [showBaseline, setShowBaseline]   = useState(false);
  const [showRefs, setShowRefs]           = useState(false);

  const hasUncertainty = a.uncertainty_pct != null;

  return (
    <div className="rounded-xl border bg-white overflow-hidden"
      style={{ borderColor: a.color + "40" }}
      data-testid={`sdg-card-${a.sdg}`}>
      {/* Header */}
      <div className="flex items-start gap-3 px-4 py-3"
        style={{ backgroundColor: a.color + "10" }}>
        <SDGBadge sdg={a.sdg} color={a.color} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <p className="text-xs font-bold text-[#0F172A]">SDG {a.sdg} — {a.title}</p>
            <ConfidenceBadge level={a.confidence} />
            {a.data_maturity && <DataMaturityBadge level={a.data_maturity} />}
          </div>
          <p className="text-[10px] text-slate-600">Target {a.target} · {a.contribution}</p>
        </div>
      </div>

      {/* Metrics */}
      <div className="px-4 py-3 space-y-2">
        {/* Primary metric — always tCO₂e or physical unit */}
        {a.primary_metric !== null && a.primary_metric !== undefined && (
          <div className="flex items-start gap-2">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide w-16 flex-shrink-0 mt-0.5">Primary</span>
            <div>
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <span className="text-sm font-bold text-[#0F172A] tabular-nums">
                  {typeof a.primary_metric === 'number' ? a.primary_metric.toLocaleString() : a.primary_metric}
                </span>
                {hasUncertainty && (
                  <span className="text-[9px] text-amber-600 font-semibold">
                    ± {a.uncertainty_pct}%
                  </span>
                )}
              </div>
              <p className="text-[10px] text-slate-500">{a.primary_unit}</p>
              {hasUncertainty && a.data_maturity === "Estimated" && (
                <p className="text-[9px] text-orange-500 mt-0.5 italic">
                  Model-based estimate — range narrows with site-measured data
                </p>
              )}
            </div>
          </div>
        )}

        {/* Intensity metric */}
        {a.intensity_metric !== null && a.intensity_metric !== undefined && (
          <div className="flex items-start gap-2">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide w-16 flex-shrink-0 mt-0.5">Intensity</span>
            <div>
              <span className="text-sm font-bold text-teal-700 tabular-nums">
                {typeof a.intensity_metric === 'number' ? a.intensity_metric.toLocaleString() : a.intensity_metric}
              </span>
              <p className="text-[10px] text-slate-500">{a.intensity_unit}</p>
            </div>
          </div>
        )}

        {/* Secondary (illustrative) metric */}
        {a.secondary_metric !== null && a.secondary_metric !== undefined && (
          <div className="flex items-start gap-2">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide w-16 flex-shrink-0 mt-0.5">Illustrative</span>
            <div>
              <span className="text-xs font-semibold text-slate-500 tabular-nums">≈ {a.secondary_metric?.toLocaleString()}</span>
              <p className="text-[9px] text-slate-400 italic">{a.secondary_unit}</p>
            </div>
          </div>
        )}

        {/* NDC note */}
        {a.ndc_note && (
          <div className="flex items-start gap-1.5 text-[9px] text-slate-500 bg-teal-50 rounded-lg px-2 py-1.5 border border-teal-100">
            <Shield size={10} className="text-teal-500 flex-shrink-0 mt-0.5" />
            {a.ndc_note}
          </div>
        )}

        {/* Uncertainty range — expandable */}
        {hasUncertainty && (
          <div className="border border-amber-100 rounded-lg overflow-hidden">
            <button
              onClick={() => {}}
              className="w-full flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-[9px] text-amber-700 font-semibold text-left cursor-default"
            >
              <AlertTriangle size={9} className="flex-shrink-0" />
              Uncertainty: ±{a.uncertainty_pct}% on this estimate
            </button>
            <p className="px-3 py-2 text-[9px] text-slate-500 leading-snug">{a.uncertainty_drivers}</p>
          </div>
        )}

        {/* Evidence fields — expandable */}
        <div>
          <button onClick={() => setShowEvidence(!showEvidence)}
            className="flex items-center gap-1 text-[9px] text-teal-600 font-semibold hover:underline">
            <ChevronDown size={10} className={`transition-transform ${showEvidence ? "rotate-180" : ""}`} />
            {showEvidence ? "Hide" : "View"} evidence inputs ({a.evidence_fields?.length || 0} data points)
          </button>
          {showEvidence && a.evidence_fields?.length > 0 && (
            <div className="mt-1.5 space-y-0.5">
              {a.evidence_fields.map((e, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[9px] text-slate-500">
                  <span className="text-teal-400 font-bold flex-shrink-0">{i + 1}.</span>
                  {e}
                </div>
              ))}
              <p className="text-[8px] text-slate-400 italic mt-1">
                Confidence: {a.confidence} — {a.confidence_note}
              </p>
              {a.data_maturity_note && (
                <p className="text-[8px] text-orange-500 italic mt-0.5">
                  Data Status ({a.data_maturity}): {a.data_maturity_note}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Full Attribution Panel ─────────────────────────────────────────────────
function SDGFullPanel({ sdgData, compact = false }) {
  const [expanded, setExpanded]           = useState(!compact);
  const [showBoundary, setShowBoundary]   = useState(false);
  const [showBaseline, setShowBaseline]   = useState(false);
  const [showRefs, setShowRefs]           = useState(false);
  const [showUpgrade, setShowUpgrade]     = useState(false);
  const [tracked, setTracked]             = useState(false);  // fire-and-forget

  // Track impact_reviewed when panel is first expanded
  const handleExpand = () => {
    if (!expanded && !tracked && sdgData?.attributed_sdgs?.length > 0) {
      // Fire-and-forget tracking
      import("@/api/client").then(m => m.default.post("/activation/track/impact_reviewed").catch(() => {}));
      setTracked(true);
    }
    setExpanded(e => !e);
  };

  // Auto-track when not compact (panel auto-expanded)
  useEffect(() => {
    if (!compact && !tracked && sdgData?.attributed_sdgs?.length > 0) {
      import("@/api/client").then(m => m.default.post("/activation/track/impact_reviewed").catch(() => {}));
      setTracked(true);
    }
  }, [sdgData, compact, tracked]);

  if (!sdgData?.attributed_sdgs?.length) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-start gap-2">
        <Info size={13} className="text-slate-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-slate-500">
          SDG attribution is computed after project type and emission data are entered. It cannot be manually assigned.
        </p>
      </div>
    );
  }

  const highCount = sdgData.high_confidence_count || sdgData.attributed_sdgs.filter(a => a.confidence === "High").length;
  const maturity  = sdgData.overall_data_maturity || "Estimated";
  const maturityC = DATA_MATURITY_CONFIG[maturity] || DATA_MATURITY_CONFIG.Estimated;
  const boundary  = sdgData.system_boundary;
  const baseline  = sdgData.baseline_justification;
  const refs      = sdgData.evidence_references || [];

  return (
    <div className="space-y-4" data-testid="sdg-full-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold text-[#0F172A] mb-0.5">Sustainable Development Impact</p>
          <p className="text-[10px] text-slate-500">{sdgData.summary}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {highCount > 0 && (
              <p className="text-[9px] text-emerald-600">
                {highCount} High-confidence attribution{highCount !== 1 ? "s" : ""} — directly quantified
              </p>
            )}
            <DataMaturityBadge level={maturity} />
          </div>
        </div>
        {compact && (
          <button onClick={handleExpand}
            className="text-[10px] text-teal-600 font-semibold hover:underline flex items-center gap-1 flex-shrink-0">
            <ChevronDown size={11} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
            {expanded ? "Hide" : "View"} full attribution
          </button>
        )}
      </div>

      {/* Badge row — always visible */}
      <SDGSummaryRow sdgData={sdgData} />

      {/* Detailed cards — expandable */}
      {expanded && (
        <>
          <div className="grid grid-cols-1 gap-4" data-testid="sdg-attribution-cards">
            {sdgData.attributed_sdgs.map(a => (
              <SDGAttributionCard key={a.sdg} a={a} />
            ))}
          </div>

          {/* Mapping table — for institutional / PDF use */}
          <div className="border border-slate-200 rounded-xl overflow-hidden text-[11px]"
            data-testid="sdg-mapping-table">
            <div className="grid gap-3 bg-slate-50 px-4 py-2.5 text-[9px] text-slate-500 uppercase tracking-widest font-bold border-b border-slate-200"
              style={{ gridTemplateColumns: "1fr 1fr 80px 60px 60px" }}>
              <span>Action → Outcome</span><span>Evidence Basis (Summary)</span>
              <span className="text-center">SDG</span><span className="text-center">Target</span><span className="text-center">Confidence</span>
            </div>
            {sdgData.attributed_sdgs.map((a, i) => (
              <div key={a.sdg}
                className={`grid gap-3 px-4 py-3 border-b border-slate-100 last:border-0 ${i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}`}
                style={{ gridTemplateColumns: "1fr 1fr 80px 60px 60px" }}>
                <div>
                  <p className="text-slate-700 font-medium">{a.chain?.action}</p>
                  <p className="text-slate-500 text-[10px]">{a.chain?.outcome}</p>
                </div>
                <p className="text-slate-500 text-[10px] leading-snug">
                  {a.evidence_fields?.slice(0, 2).join(" · ")}
                </p>
                <div className="flex justify-center">
                  <SDGBadge sdg={a.sdg} color={a.color} size="sm" />
                </div>
                <span className="text-center font-mono text-slate-600">{a.target}</span>
                <div className="flex justify-center">
                  <ConfidenceBadge level={a.confidence} />
                </div>
              </div>
            ))}
          </div>

          {/* ── NEW: System Boundary ─────────────────────────────────── */}
          {boundary && (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setShowBoundary(!showBoundary)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                data-testid="sdg-boundary-toggle"
              >
                <div className="flex items-center gap-2">
                  <Layers size={12} className="text-slate-500" />
                  <span className="text-xs font-bold text-slate-700">System Boundary Definition</span>
                  <span className="text-[9px] text-slate-400">(included vs excluded)</span>
                </div>
                <ChevronDown size={12} className={`text-slate-400 transition-transform ${showBoundary ? "rotate-180" : ""}`} />
              </button>
              {showBoundary && (
                <div className="px-4 py-3 space-y-3 bg-white">
                  <div>
                    <p className="text-[9px] font-bold text-emerald-700 uppercase tracking-wide mb-1.5">Included</p>
                    <div className="space-y-1">
                      {(boundary.included || []).map((item, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-[10px] text-slate-600">
                          <span className="text-emerald-500 font-bold flex-shrink-0 mt-0.5">+</span>
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Excluded</p>
                    <div className="space-y-1">
                      {(boundary.excluded || []).map((item, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-[10px] text-slate-500">
                          <span className="text-slate-400 font-bold flex-shrink-0 mt-0.5">-</span>
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                  {boundary.boundary_note && (
                    <p className="text-[9px] text-slate-400 italic border-t border-slate-100 pt-2">{boundary.boundary_note}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── NEW: Baseline Justification ──────────────────────────── */}
          {baseline && (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setShowBaseline(!showBaseline)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                data-testid="sdg-baseline-toggle"
              >
                <div className="flex items-center gap-2">
                  <Shield size={12} className="text-slate-500" />
                  <span className="text-xs font-bold text-slate-700">Baseline Justification</span>
                  <span className="text-[9px] text-slate-400">(additionality argument)</span>
                </div>
                <ChevronDown size={12} className={`text-slate-400 transition-transform ${showBaseline ? "rotate-180" : ""}`} />
              </button>
              {showBaseline && (
                <div className="px-4 py-3 space-y-3 bg-white">
                  <div className="grid grid-cols-1 gap-2.5">
                    <div>
                      <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wide mb-1">Baseline Scenario</p>
                      <p className="text-[10px] text-slate-700">{baseline.baseline_scenario}</p>
                    </div>
                    <div>
                      <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wide mb-1">Additionality Argument</p>
                      <p className="text-[10px] text-slate-700">{baseline.additionality_basis}</p>
                    </div>
                    <div>
                      <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wide mb-1">Counterfactual</p>
                      <p className="text-[10px] text-slate-600 italic">{baseline.counterfactual}</p>
                    </div>
                    {baseline.regulatory_context && (
                      <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                        <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wide mb-1">Regulatory Context</p>
                        <p className="text-[9px] text-slate-500">{baseline.regulatory_context}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── NEW: Evidence References (IPCC / Standards) ──────────── */}
          {refs.length > 0 && (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setShowRefs(!showRefs)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                data-testid="sdg-refs-toggle"
              >
                <div className="flex items-center gap-2">
                  <BookOpen size={12} className="text-slate-500" />
                  <span className="text-xs font-bold text-slate-700">Evidence References</span>
                  <span className="text-[9px] text-slate-400">({refs.length} standard{refs.length !== 1 ? "s" : ""} cited)</span>
                </div>
                <ChevronDown size={12} className={`text-slate-400 transition-transform ${showRefs ? "rotate-180" : ""}`} />
              </button>
              {showRefs && (
                <div className="px-4 py-3 space-y-3 bg-white">
                  {refs.map((ref, i) => (
                    <div key={i} className="border-b border-slate-100 last:border-0 pb-2.5 last:pb-0">
                      <p className="text-[10px] font-bold text-slate-700">{ref.source}</p>
                      <p className="text-[9px] text-teal-600 font-semibold mt-0.5">{ref.section}</p>
                      <p className="text-[9px] text-slate-500 mt-0.5">{ref.relevance}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Lifetime equivalence */}
          {sdgData.lifetime_cars_equivalent > 0 && (
            <div className="bg-[#0F172A] rounded-xl px-4 py-3 flex items-center gap-3">
              <div>
                <p className="text-xs font-bold text-white">Lifetime Climate Equivalent</p>
                <p className="text-[11px] text-slate-300 mt-0.5">
                  {sdgData.lifetime_cars_equivalent.toLocaleString()} passenger vehicle-years avoided over project life
                </p>
                <p className="text-[9px] text-slate-500 mt-0.5 italic">{sdgData.lifetime_cars_note}</p>
              </div>
            </div>
          )}

          {/* ── GAP 4: Data Maturity Upgrade Path ───────────────────── */}
          {sdgData.upgrade_path && sdgData.upgrade_path.next_level && (
            <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="sdg-upgrade-path">
              <button
                onClick={() => setShowUpgrade(!showUpgrade)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-gradient-to-r from-teal-50 to-slate-50 hover:from-teal-100 transition-colors text-left"
              >
                <div className="flex items-center gap-2">
                  <ArrowUpCircle size={12} className="text-teal-600" />
                  <span className="text-xs font-bold text-teal-700">
                    How to Upgrade: {sdgData.upgrade_path.current_status} → {sdgData.upgrade_path.next_level}
                  </span>
                  <span className="text-[9px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full font-semibold">
                    Data Maturity
                  </span>
                </div>
                <ChevronDown size={12} className={`text-slate-400 transition-transform ${showUpgrade ? "rotate-180" : ""}`} />
              </button>
              {showUpgrade && sdgData.upgrade_path.requirements && (
                <div className="px-4 py-3 bg-white space-y-3">
                  <div>
                    <p className="text-[9px] font-bold text-teal-700 uppercase tracking-wide mb-1.5">
                      Required Steps — {sdgData.upgrade_path.requirements.label}
                    </p>
                    <div className="space-y-1.5">
                      {(sdgData.upgrade_path.requirements.steps || []).map((step, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="flex-shrink-0 w-4 h-4 rounded-full bg-teal-100 text-teal-700 text-[8px] font-bold flex items-center justify-center mt-0.5">
                            {i + 1}
                          </span>
                          <p className="text-[10px] text-slate-600">{step}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {sdgData.upgrade_path.requirements.estimated_cost && (
                      <div className="bg-slate-50 rounded-lg p-2 border border-slate-100">
                        <p className="text-[8px] font-bold text-slate-500 uppercase mb-0.5">Est. Cost</p>
                        <p className="text-[10px] text-slate-700">{sdgData.upgrade_path.requirements.estimated_cost}</p>
                      </div>
                    )}
                    {sdgData.upgrade_path.requirements.time_to_upgrade && (
                      <div className="bg-slate-50 rounded-lg p-2 border border-slate-100">
                        <p className="text-[8px] font-bold text-slate-500 uppercase mb-0.5">Timeline</p>
                        <p className="text-[10px] text-slate-700">{sdgData.upgrade_path.requirements.time_to_upgrade}</p>
                      </div>
                    )}
                  </div>
                  {sdgData.upgrade_path.beyond && (
                    <div className="border-t border-slate-100 pt-2">
                      <p className="text-[8px] text-slate-400 font-semibold uppercase mb-1">
                        Next: {sdgData.upgrade_path.beyond.label}
                      </p>
                      <p className="text-[9px] text-slate-500">
                        {(sdgData.upgrade_path.beyond.steps || []).slice(0, 2).join(" · ")}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <p className="text-[9px] text-slate-400 italic">{sdgData.disclaimer}</p>

          {/* ── GAP 1: Versioning strip ──────────────────────────────── */}
          {sdgData.engine_version && (
            <div className="flex items-start gap-2 px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl"
              data-testid="sdg-versioning-strip">
              <Tag size={10} className="text-slate-400 flex-shrink-0 mt-0.5" />
              <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                <span className="text-[9px] text-slate-500">
                  <span className="font-bold">Engine:</span> {sdgData.engine_version}
                </span>
                {sdgData.methodology_version && (
                  <span className="text-[9px] text-slate-500">
                    <span className="font-bold">Methodology:</span> {sdgData.methodology_version}
                  </span>
                )}
                <span className="text-[9px] text-slate-500">
                  <span className="font-bold">Standard:</span> {sdgData.primary_standard}
                </span>
                {sdgData.generated_at && (
                  <span className="text-[9px] text-slate-500">
                    <span className="font-bold">Generated:</span> {sdgData.generated_at}
                  </span>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export { SDGBadge, SDGSummaryRow, SDGFullPanel, ConfidenceBadge };
